import {FC, useEffect, useState} from 'react'
import {CustomerEditModalHeader} from './CustomerEditModalHeader'
import {useListView} from '../core/ListViewProvider'
import {QUERIES, isNotEmpty} from '../../../../../../_metronic/helpers'
import {useQuery} from 'react-query'
import {getCustomerById} from '../core/_requests'
import {CustomerEditModalForm} from './CustomerEditModalForm'
import {useQueryResponse} from '../core/QueryResponseProvider'
import {CustomerEditType} from './CustomerEditType'
import {Customer} from '../core/_models'
import {useIsOnPremises} from '../../../../auth/core/useOnPremises'
import {CustomerTaxFileLookup} from './CustomerTaxFileLookup'
import {
  BuyerPrefill,
  BuyerTaxFile,
  BuyerTaxFileFailure,
  isLoggedInTaxWorkspace,
  placeIdentifier,
  prefillFromTaxFile,
  typeOfIdentifierWithoutFile,
} from './buyerTaxFile'
import './CustomerTaxFileLookup.css'

type props = {
  callBack?: (data: Customer | undefined) => void
  closeFn?: () => void
}

const UNKNOWN_TYPE_NOTICE: BuyerTaxFileFailure = {
  correctable: false,
  message: 'نوع خریدار از روی شناسه‌های این پرونده مشخص نشد؛ نوع خریدار را انتخاب کنید.',
}

const CustomerEditModal: FC<props> = ({callBack, closeFn}) => {
  const {setvariableA, itemIdForUpdate, setItemIdForUpdate} = useListView()
  const {refetch} = useQueryResponse()
  const [type, setType] = useState<number | undefined>()
  // A new buyer starts from the tax-file inquiry (2026-10-22). An on-premises install has no کارپوشه, so there the
  // type comes first, as it always did.
  const onPremises = useIsOnPremises()
  const [step, setStep] = useState<'lookup' | 'type'>(onPremises ? 'type' : 'lookup')
  const [identifier, setIdentifier] = useState('')
  const [prefill, setPrefill] = useState<BuyerPrefill>()
  const [notice, setNotice] = useState<BuyerTaxFileFailure>()

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
      setvariableA(undefined)
      setItemIdForUpdate(undefined)
    }
  }, [])

  const enabledQuery: boolean = isNotEmpty(itemIdForUpdate)
  const {data: customer} = useQuery(
    `${QUERIES.CUSTOMERS_LIST}-customer-${itemIdForUpdate}`,
    () => {
      return getCustomerById(itemIdForUpdate)
    },
    {
      cacheTime: 0,
      enabled: enabledQuery,
      onError: (err) => {
        setItemIdForUpdate(undefined)
        console.error(err)
      },
    }
  )

  const chooseFile = (file: BuyerTaxFile) => {
    setPrefill(prefillFromTaxFile(file))
    if (file.buyerType >= 1 && file.buyerType <= 4) {
      setNotice(undefined)
      setType(file.buyerType)
    } else {
      setNotice(UNKNOWN_TYPE_NOTICE)
      setStep('type')
    }
  }

  // No file: a person's national code or a foreigner's code says the type; other identifiers leave it to the user.
  const continueWithoutFile = (value: string) => {
    setIdentifier(value)
    setPrefill(undefined)
    setNotice(undefined)
    const likely = typeOfIdentifierWithoutFile(value)
    if (likely) setType(likely)
    else setStep('type')
  }

  const fallBack = (value: string, failure: BuyerTaxFileFailure) => {
    setIdentifier(value)
    setPrefill(undefined)
    setNotice(failure.silent ? undefined : failure)
    setStep('type')
  }

  const enterManually = (value: string) => {
    setIdentifier(value)
    setPrefill(undefined)
    setNotice(undefined)
    setStep('type')
  }

  const searchAgain = () => {
    setType(undefined)
    setPrefill(undefined)
    setNotice(undefined)
    setStep('lookup')
  }

  // The chosen file's identifiers, or the typed one placed by the type the user chose.
  const formPrefill = prefill ?? (type ? placeIdentifier(type, identifier) : undefined)
  const closeAfterSave = () => {
    refetch()
    setvariableA(undefined)
    setItemIdForUpdate(undefined)
  }

  return (
    <>
      <div
        className='modal fade show d-block mp-catalog-modal'
        id='kt_modal_add_customer'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
      >
        {/* begin::Modal dialog */}
        <div className='modal-dialog modal-dialog-centered modal-xl modal-fullscreen-md-down'>
          {/* begin::Modal content */}
          <div className='modal-content'>
            {itemIdForUpdate ? (
              customer && (
                <CustomerEditModalHeader type={type} customer={customer} closeFn={closeFn} />
              )
            ) : (
              <CustomerEditModalHeader type={type} closeFn={closeFn} />
            )}
            {/* begin::Modal body */}
            <div className='modal-body scroll-y mx-5 mx-xl-15 my-7 mp-catalog-modal-body'>
              {type || isNotEmpty(itemIdForUpdate) ? (
                <>
                  {itemIdForUpdate ? (
                    customer && (
                      <CustomerEditModalForm
                        type={{type: type, setType: setType}}
                        customer={customer}
                        callBack={callBack ? callBack : closeAfterSave}
                      />
                    )
                  ) : (
                    <>
                      {!onPremises && (
                        <div className='text-end mb-4'>
                          <button
                            type='button'
                            className='btn btn-link btn-sm p-0 fs-7'
                            onClick={searchAgain}
                          >
                            <i className='las la-search me-1' aria-hidden='true' />
                            استعلام پرونده خریدار دیگر
                          </button>
                        </div>
                      )}
                      <CustomerEditModalForm
                        type={{type: type, setType: setType}}
                        prefill={formPrefill}
                        callBack={callBack ? callBack : closeAfterSave}
                      />
                    </>
                  )}
                </>
              ) : step === 'lookup' ? (
                <CustomerTaxFileLookup
                  initialIdentifier={identifier}
                  onChoose={chooseFile}
                  onWithoutFile={continueWithoutFile}
                  onFallback={fallBack}
                  onManual={enterManually}
                />
              ) : (
                <>
                  {notice && (
                    <div className='mp-buyer-lookup-fallback' role='status'>
                      <i className='las la-info-circle fs-2 text-warning' aria-hidden='true' />
                      <div>
                        {notice.message}
                        {notice.traceId && (
                          <span className='d-block text-gray-600 fs-8 mt-1'>
                            شناسه پیگیری: <bdi dir='ltr'>{notice.traceId}</bdi>
                            {isLoggedInTaxWorkspace(notice) && (
                              <>
                                {'، '}
                                <a
                                  href={`/tax-workspace/logs?cid=${encodeURIComponent(notice.traceId)}`}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  جزئیات در گزارش کارپوشه
                                </a>
                              </>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <CustomerEditType type={{type: type, setType: setType}} />
                  {!onPremises && (
                    <div className='mt-6'>
                      <button
                        type='button'
                        className='btn btn-link btn-sm p-0 fs-7'
                        onClick={searchAgain}
                      >
                        <i className='las la-angle-right me-1' aria-hidden='true' />
                        {notice && !prefill
                          ? 'تلاش دوباره برای استعلام پرونده'
                          : 'بازگشت به استعلام پرونده'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            {/* end::Modal body */}
          </div>
          {/* end::Modal content */}
        </div>
        {/* end::Modal dialog */}
      </div>
      {/* begin::Modal Backdrop */}
      <div className='modal-backdrop fade show'></div>
      {/* end::Modal Backdrop */}
    </>
  )
}

export {CustomerEditModal}
